// ProfilePictureUpload.jsx
import React, { useState } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api';
import { toast } from 'react-hot-toast';

function ProfilePictureUpload({ onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Check file size
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File size too large. Maximum size is 10MB.');
      return;
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    try {
      setUploading(true);
      setError('');

      console.log('Starting upload process for file:', selectedFile.name);
      
      // Generate a unique public_id
      const public_id = `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const timestamp = Math.round((new Date).getTime()/1000);
      
      // Request signature from backend
      console.log('Requesting signature from backend...');
      const signatureResponse = await axios.post(API_CONFIG.getUrl('generateSignature'), {
        folder: 'profile_pictures',
        public_id,
        timestamp
      });
      
      // Extract the signature from the response
      const { signature } = signatureResponse.data;
      
      if (!signature) {
        throw new Error('Failed to generate signature: ' + (signatureResponse.data.message || 'Unknown error'));
      }
      
      console.log('Signature received:', signature);
      
      // Prepare form data for upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);
      formData.append('timestamp', timestamp);
      formData.append('public_id', public_id);
      formData.append('signature', signature);
      formData.append('folder', 'profile_pictures');
      
      // Upload to Cloudinary
      console.log('Uploading to Cloudinary with cloud name:', process.env.REACT_APP_CLOUDINARY_CLOUD_NAME);
      const uploadResponse = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (!uploadResponse.data.secure_url) {
        throw new Error('Upload response missing secure_url');
      }
      
      console.log('Upload successful:', {
        secure_url: uploadResponse.data.secure_url,
        public_id: uploadResponse.data.public_id
      });
      
      // Update user profile with new image URL
      await onUpdate(uploadResponse.data.secure_url);
      
      // Show success message
      toast.success('Profile picture updated successfully!');
      
    } catch (error) {
      console.log('Upload error:', error);
      
      let errorMessage = 'Failed to upload profile picture. ';
      if (error.response?.data?.error?.message) {
        errorMessage += error.response.data.error.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        id="profile-pic-input"
      />
      <label 
        htmlFor="profile-pic-input" 
        className="btn btn-primary change-profile-pic-btn"
        style={{
          padding: '8px 16px',
          backgroundColor: '#6c5ce7',
          color: 'white',
          borderRadius: '4px',
          cursor: 'pointer',
          display: 'inline-block',
          fontSize: '14px',
          fontWeight: '500',
          border: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease'
        }}
      >
        {uploading ? 'Uploading...' : 'Change Picture'}
      </label>
      {error && <p style={{ color: 'red' }}>{error}</p>}
    </div>
  );
}

export default ProfilePictureUpload;
